import { HomepageSettings } from "@/types/api";

// Environment configuration
export const ENV = {
  NODE_ENV: process.env.NODE_ENV,
  isDevelopment: process.env.NODE_ENV === "development",
  isProduction: process.env.NODE_ENV === "production",
} as const;

// API configuration
export const API_CONFIG = {
  baseUrl: process.env.NEXT_PUBLIC_API_BASE_URL || "",
  timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || "30000"),
  retries: parseInt(process.env.NEXT_PUBLIC_API_RETRIES || "3"),
} as const;

// Feature flags
export const FEATURES = {
  enableInstagram: process.env.NEXT_PUBLIC_ENABLE_INSTAGRAM === "true",
  enableAnalytics: process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === "true",
  enableErrorReporting:
    process.env.NEXT_PUBLIC_ENABLE_ERROR_REPORTING === "true",
  enableDebugLogs: process.env.NEXT_PUBLIC_ENABLE_DEBUG_LOGS === "true",
} as const;

// UI configuration
export const UI_CONFIG = {
  defaultTheme: "#B980B8",
  animationDuration: 300,
  debounceTime: 500,
  maxFileSize: 5 * 1024 * 1024, // 5MB - 중앙 집중식 파일 크기 제한
  supportedImageTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
  autoSaveInterval: 2 * 60 * 1000, // 2 minutes
} as const;

// Default homepage settings
export const DEFAULT_HOMEPAGE_SETTINGS: HomepageSettings = {
  uxCaseStudies: {
    enabled: true,
    title: "UX CASE STUDIES",
    columns: 4,
    rows: 1,
    maxItems: 4,
    featuredPosts: [],
  },
  digitalMarketing: {
    enabled: true,
    title: "DIGITAL MARKETING",
    featuredTags: [
      "social-media",
      "branding",
      "content-marketing"
    ],
    featuredPosts: [],
  },
  artDirection: {
    enabled: true,
    title: "ART DIRECTION",
    maxItems: 4,
    featuredPosts: [],
  },
  creativeWorks: {
    enabled: true,
    title: "CREATIVE CORNERS",
    columns: 6,
    rows: 1,
    maxItems: 6,
    username: "gitta_creative",
  },
  oddsAndEnds: {
    enabled: true,
    title: "ODDS AND ENDS",
    maxItems: 3,
    featuredPosts: [],
  },
};

// Validation helpers
export const VALIDATION = {
  slug: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^https?:\/\/.+/,
  maxTitleLength: 100,
  maxDescriptionLength: 500,
  maxTagsCount: 10,
  maxTagLength: 30,
} as const;

// Cache configuration
export const CACHE_CONFIG = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  staleWhileRevalidate: 10 * 60 * 1000, // 10 minutes
} as const;

// Error configuration
export const ERROR_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  exponentialBackoff: true,
  timeout: 30000,
} as const;

// Performance configuration
export const PERFORMANCE_CONFIG = {
  lazy: {
    rootMargin: "100px", // Load components 100px before they come into view
    threshold: 0.1,
  },
  imageOptimization: {
    quality: 85,
    formats: ["webp", "avif"],
    sizes: "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  },
} as const;

// Development-only configuration
export const DEV_CONFIG = ENV.isDevelopment
  ? ({
      showPerformanceMetrics: true,
      enableHotReload: true,
      debugMode: true,
      mockApi: false,
    } as const)
  : ({} as const);

// Configuration validation function
export function validateConfig() {
  const requiredEnvVars = [];

  if (FEATURES.enableAnalytics && !process.env.NEXT_PUBLIC_GA_ID) {
    requiredEnvVars.push("NEXT_PUBLIC_GA_ID");
  }

  if (FEATURES.enableErrorReporting && !process.env.NEXT_PUBLIC_SENTRY_DSN) {
    requiredEnvVars.push("NEXT_PUBLIC_SENTRY_DSN");
  }

  if (requiredEnvVars.length > 0) {
    console.warn(
      `Missing required environment variables for enabled features: ${requiredEnvVars.join(
        ", "
      )}`
    );
  }
}

// Initialize configuration
if (typeof window === "undefined") {
  // Server-side initialization
  validateConfig();
}

// Utility functions
export function getConfig<T = string | number | boolean | object>(
  key: string, 
  defaultValue?: T
): T | undefined {
  // Generic configuration getter for dynamic access
  const configs = {
    ENV,
    API_CONFIG,
    FEATURES,
    UI_CONFIG,
    VALIDATION,
    CACHE_CONFIG,
    ERROR_CONFIG,
    PERFORMANCE_CONFIG,
    DEV_CONFIG,
  };

  const keys = key.split(".");
  let value: any = configs;

  for (const k of keys) {
    if (value && typeof value === "object" && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return defaultValue;
    }
  }

  return value as T;
}

export function isFeatureEnabled(feature: keyof typeof FEATURES): boolean {
  return FEATURES[feature] ?? false;
}

export function isDevelopment(): boolean {
  return ENV.isDevelopment;
}

export function isProduction(): boolean {
  return ENV.isProduction;
}

// Type exports for better TypeScript support
export type Config = typeof ENV &
  typeof API_CONFIG &
  typeof FEATURES &
  typeof UI_CONFIG;
export type Feature = keyof typeof FEATURES;
